/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/

import { useState, useEffect } from 'react';
import axios from "../http/axios";
import { AssetInterface } from '@malmur-technologies/medicalsystem-shared';
import { getAssetUrl, shouldUseProxy } from 'utils/blobProxy.utils';

// In-memory cache for blob URLs during session
const blobUrlCache = new Map<string, string>();

/**
 * Hook for loading authenticated assets (images, videos, etc.)
 * 
 * When blob proxy is enabled, assets require authentication (JWT token).
 * DOM elements (<img>, <iframe>, <video>) cannot send custom headers,
 * so we need to:
 * 1. Fetch binary data using axios with headers (Authorization + x-resource-id)
 * 2. Create a Blob object URL
 * 3. Use that URL in the DOM element
 * 
 * Flow:
 * axios.get(url, { headers: { 'x-resource-id': assetId } })
 *   → Backend validates JWT + extracts resource ID from header
 *   → Backend fetches from Azure
 *   → Returns binary data
 *   → Hook creates blob URL
 *   → DOM element uses blob URL
 * 
 * Note: We use axios directly instead of useHttp because:
 * - We need responseType: 'blob' for binary data
 * - useHttp is designed for JSON responses
 * - Our axios instance already has auth interceptors configured
 * 
 * @param asset - Asset to load (can be undefined or partial for cases where asset might not exist or be incomplete)
 * @returns Object with blobUrl, isLoading, and error state
 */
export const useAuthenticatedAsset = (asset?: Partial<AssetInterface>, refreshKey?: number) => {
  const [blobUrl, setBlobUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);

  useEffect(() => {
    let objectUrl: string | null = null;
    let isFromCache = false;
    let cancelled = false;

    const loadAsset = async () => {
      // Clear previous blob URL when asset changes
      setBlobUrl('');
      
      // If no asset provided, clear state
      if (!asset) {
        setIsLoading(false);
        return;
      }

      // If proxy is disabled, use direct URL (with SAS token)
      if (!shouldUseProxy()) {
        setBlobUrl(asset.url || '');
        return;
      }
      
      // Check if blob URL is already cached
      const cacheKey = asset.id || asset.url || '';
      if (cacheKey && blobUrlCache.has(cacheKey)) {
        const cachedUrl = blobUrlCache.get(cacheKey)!;
        setBlobUrl(cachedUrl);
        setProgress(100);
        setIsLoading(false);
        isFromCache = true;
        return;
      }
      
      setIsLoading(true);
      setError(null);
      setProgress(0);

      try {
        // Get the proxy URL and headers
        // Safe to cast since we've checked asset exists and has required fields above
        const { url: proxyUrl, headers: proxyHeaders } = getAssetUrl(asset as AssetInterface);
        // Fetch with axios (auth headers added by interceptors, resource ID in headers)
        const response = await axios.get(proxyUrl, {
          responseType: 'blob', // Important: get binary data
          headers: {
            ...proxyHeaders,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          onDownloadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setProgress(percentCompleted);
            }
          },
        });
        if (cancelled) return;

        // Create blob URL from response
        const blob = new Blob([response.data], { 
          type: response.headers['content-type'] || asset.mimetype || 'application/octet-stream'
        });
        
        // Revoke previous blob URL if it exists to free memory
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
        
        objectUrl = URL.createObjectURL(blob);
        
        // Cache the blob URL for this asset
        if (cacheKey) {
          blobUrlCache.set(cacheKey, objectUrl);
        }
        
        setBlobUrl(objectUrl);
        setProgress(100);
        setIsLoading(false);
      } catch (err: any) {
        if (cancelled) return;      
        // Si la respuesta es un Blob, intenta leerlo para ver el mensaje de error real
        if (err?.response?.data instanceof Blob) {
          const reader = new FileReader();
          reader.onload = () => {
            setError(reader.result as string);
          };
          reader.readAsText(err.response.data);
        }
        
        setError(err?.message || 'Failed to load asset');
        setIsLoading(false);
      }
    };

    loadAsset();

    // Cleanup: revoke blob URL on unmount ONLY if it's not cached
    return () => {
      cancelled = true;
      if (objectUrl && !isFromCache) {
        // Don't revoke if it's in cache, it will be reused
        const cacheKey = asset?.id || asset?.url || '';
        if (!cacheKey || !blobUrlCache.has(cacheKey)) {
          URL.revokeObjectURL(objectUrl);
        }
      }
    };
  }, [asset?.id, asset?.url, refreshKey]); // Re-load if asset or refreshKey changes

  return { blobUrl, isLoading, error, progress };
};
